import type { FlattenedStep } from './types';

/**
 * Compute a left-to-right topological level for each step in a set.
 * Only edges between steps within the given set are considered (used to
 * lay out root steps and nested steps independently).
 */
export const computeLevels = (entries: FlattenedStep[]): Map<string, number> => {
  const idSet = new Set(entries.map((e) => e.step.id));
  const successors = new Map<string, Set<string>>();
  const indegree = new Map<string, number>();

  idSet.forEach((id) => {
    successors.set(id, new Set());
    indegree.set(id, 0);
  });

  entries.forEach(({ step }) => {
    step.runAfter.forEach(({ stepId: beforeId }) => {
      if (!beforeId || !idSet.has(beforeId)) {
        return;
      }

      const succ = successors.get(beforeId);
      if (!succ) return;
      succ.add(step.id);
      indegree.set(step.id, (indegree.get(step.id) ?? 0) + 1);
    });
  });

  const level = new Map<string, number>();
  const queue: string[] = [];

  idSet.forEach((id) => {
    if ((indegree.get(id) ?? 0) === 0) {
      queue.push(id);
      level.set(id, 0);
    }
  });

  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined) break;
    const currentLevel = level.get(id) ?? 0;
    successors.get(id)?.forEach((nextId) => {
      const nextLevel = currentLevel + 1;
      const existing = level.get(nextId);
      if (existing === undefined || nextLevel > existing) {
        level.set(nextId, nextLevel);
      }

      const nextIndegree = (indegree.get(nextId) ?? 0) - 1;
      indegree.set(nextId, nextIndegree);
      if (nextIndegree === 0) {
        queue.push(nextId);
      }
    });
  }

  idSet.forEach((id) => {
    if (level.get(id) === undefined) {
      level.set(id, 0);
    }
  });

  return level;
};

/**
 * Sort nodes within each level using an iterated barycenter heuristic
 * (Sugiyama-style crossing reduction).
 *
 * Alternates forward sweeps (sort by avg y-index of predecessors) and
 * backward sweeps (sort by avg y-index of successors) for a fixed number
 * of iterations so the ordering converges toward fewer crossings.
 *
 * Returns a map of level → sorted node IDs.
 */
export const assignYOrder = (
  entries: FlattenedStep[],
  levelMap: Map<string, number>,
): Map<number, string[]> => {
  const idSet = new Set(entries.map((e) => e.step.id));

  const predecessors = new Map<string, string[]>();
  const successors = new Map<string, string[]>();
  idSet.forEach((id) => {
    predecessors.set(id, []);
    successors.set(id, []);
  });
  entries.forEach(({ step }) => {
    step.runAfter.forEach(({ stepId: beforeId }) => {
      if (!beforeId || !idSet.has(beforeId)) return;
      const preds = predecessors.get(step.id);
      const succs = successors.get(beforeId);
      if (!preds || !succs) return;
      preds.push(beforeId);
      succs.push(step.id);
    });
  });

  const byLevel = new Map<number, string[]>();
  entries.forEach(({ step }) => {
    const l = levelMap.get(step.id) ?? 0;
    const list = byLevel.get(l) ?? [];
    list.push(step.id);
    byLevel.set(l, list);
  });

  // Initial stable alphabetical seed.
  byLevel.forEach((ids) => {
    ids.sort((a, b) => a.localeCompare(b));
  });

  const sortedLevels = Array.from(byLevel.keys()).sort((a, b) => a - b);

  const getYIndex = (): Map<string, number> => {
    const m = new Map<string, number>();
    byLevel.forEach((ids) => ids.forEach((id, i) => m.set(id, i)));
    return m;
  };

  const barycenter = (
    id: string,
    neighbours: string[],
    yIndex: Map<string, number>,
  ): number | undefined => {
    const indices = neighbours
      .map((n) => yIndex.get(n))
      .filter((v): v is number => v !== undefined);
    if (indices.length === 0) return undefined;
    return indices.reduce((a, b) => a + b, 0) / indices.length;
  };

  const sortLevel = (
    level: number,
    neighboursOf: (id: string) => string[],
    yIndex: Map<string, number>,
  ): void => {
    const ids = byLevel.get(level);
    if (!ids) return;
    const ranked = ids.map((id) => ({ id, bc: barycenter(id, neighboursOf(id), yIndex) }));
    ranked.sort((a, b) => {
      if (a.bc !== undefined && b.bc !== undefined) return a.bc - b.bc || a.id.localeCompare(b.id);
      if (a.bc !== undefined) return -1;
      if (b.bc !== undefined) return 1;
      return a.id.localeCompare(b.id);
    });
    byLevel.set(
      level,
      ranked.map((r) => r.id),
    );
  };

  const ITERATIONS = 4;
  for (let pass = 0; pass < ITERATIONS; pass += 1) {
    // Forward sweep: sort by predecessors (left neighbours).
    const yFwd = getYIndex();
    sortedLevels.forEach((l) => sortLevel(l, (id) => predecessors.get(id) ?? [], yFwd));

    // Backward sweep: sort by successors (right neighbours).
    const yBwd = getYIndex();
    [...sortedLevels]
      .reverse()
      .forEach((l) => sortLevel(l, (id) => successors.get(id) ?? [], yBwd));
  }

  return byLevel;
};
